import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, AlertCircle, MapPin, Search, Plus, ChevronRight, EyeOff, Eye, FileSpreadsheet } from 'lucide-react';
import {
  usePlatformProperties,
  useHideProperty,
  useUnhideProperty,
  useImportPropertyPreview,
} from '@/hooks/api/usePlatform';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { ReviewStatusBadge } from '@/components/ui/ReviewStatusBadge';
import { AddPropertyForm, type SubmissionMeta } from '@/components/admin/AddPropertyForm';
import { AddPropertyChooser } from '@/components/admin/AddPropertyChooser';
import { BulkImportWizard } from '@/components/admin/BulkImportWizard';
import { LeadPickerDialog } from '@/components/admin/LeadPickerDialog';
import type { PropertyImportPreview, PlatformLead } from '@/services/api/platform.api';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

export function AdminPropertiesPage() {
  const navigate = useNavigate();
  const [showHidden, setShowHidden] = useState(false);
  const { data, isLoading, isError, refetch } = usePlatformProperties(showHidden);
  const hideMutation = useHideProperty();
  const unhideMutation = useUnhideProperty();
  const [search, setSearch] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [importPrefill, setImportPrefill] = useState<PropertyImportPreview | null>(null);
  const [importFilename, setImportFilename] = useState<string | null>(null);
  const [submissionMeta, setSubmissionMeta] = useState<SubmissionMeta | null>(null);
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isLeadPickerOpen, setIsLeadPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportPropertyPreview();

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    e.target.value = '';
    if (!file) return;
    try {
      const preview = await importMutation.mutateAsync(file);
      setImportPrefill(preview);
      setImportFilename(file.name);
      setSubmissionMeta(null);
      setIsAddOpen(true);
    } catch (err) {
      showToast(extractErrorMessage(err) || 'Could not read that spreadsheet.', 'error');
    }
  }

  function handleAddClose() {
    setIsAddOpen(false);
    setImportPrefill(null);
    setImportFilename(null);
    setSubmissionMeta(null);
  }

  function handleAddNew() {
    setImportPrefill(null);
    setImportFilename(null);
    setSubmissionMeta(null);
    setIsAddOpen(true);
  }

  // ── "Add Property" 3-way chooser ────────────────────────────────────────────
  function handleChooseManual() {
    setIsChooserOpen(false);
    handleAddNew();
  }

  function handleChooseImport() {
    setIsChooserOpen(false);
    handleImportClick();
  }

  function handleChooseFromSubmission() {
    setIsChooserOpen(false);
    setIsLeadPickerOpen(true);
  }

  function handleChooseBulkImport() {
    setIsChooserOpen(false);
    setIsBulkImportOpen(true);
  }

  /**
   * A picked owner lead becomes an import-preview-shaped prefill (name / city /
   * country / description) reusing AddPropertyForm's prefill mechanism; the
   * origin banner is driven by submissionMeta. Address isn't on the lead, so it
   * stays blank for the admin. Linking does NOT change the lead's status.
   */
  function handleLeadSelected(lead: PlatformLead) {
    setIsLeadPickerOpen(false);
    setImportPrefill({
      name: lead.propertyName,
      city: lead.city,
      country: lead.country ?? '',
      ...(lead.message ? { description: lead.message } : {}),
    });
    setImportFilename(null);
    setSubmissionMeta({
      ownerName: lead.ownerName,
      email: lead.email,
      phone: lead.phone,
    });
    setIsAddOpen(true);
  }

  async function toggleHidden(id: string, hidden: boolean) {
    try {
      if (hidden) {
        await unhideMutation.mutateAsync(id);
        showToast('Property is now visible');
      } else {
        await hideMutation.mutateAsync(id);
        showToast('Property hidden');
      }
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
    }
  }

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.operatorName.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.country.toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Properties"
        description="All listings across every operator, with their review state."
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileSelected}
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
            />
            <button
              type="button"
              onClick={handleImportClick}
              disabled={importMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand-700 bg-white border border-brand-300 rounded-lg hover:bg-brand-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {importMutation.isPending ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <FileSpreadsheet size={16} aria-hidden="true" />
              )}
              {importMutation.isPending ? 'Reading…' : 'Import from Excel'}
            </button>
            <button
              type="button"
              onClick={() => setIsChooserOpen(true)}
              aria-haspopup="dialog"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <Plus size={16} aria-hidden="true" />
              Add Property
            </button>
          </div>
        }
      />

      <AddPropertyChooser
        isOpen={isChooserOpen}
        onClose={() => setIsChooserOpen(false)}
        onFromSubmission={handleChooseFromSubmission}
        onManual={handleChooseManual}
        onImport={handleChooseImport}
        onBulkImport={handleChooseBulkImport}
      />

      <BulkImportWizard
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
      />

      {isLeadPickerOpen && (
        <LeadPickerDialog
          onClose={() => setIsLeadPickerOpen(false)}
          onSelect={handleLeadSelected}
        />
      )}

      <AddPropertyForm
        isOpen={isAddOpen}
        onClose={handleAddClose}
        prefill={importPrefill}
        importFilename={importFilename}
        submissionMeta={submissionMeta}
      />

      <div className="flex flex-wrap items-center gap-4">
        {data && data.length > 0 && (
          <div className="relative max-w-sm flex-1 min-w-[240px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by property, operator, city…"
              aria-label="Search properties"
              className={cn(
                'w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 bg-white',
                'placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500',
              )}
            />
          </div>
        )}
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Show hidden
        </label>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-600" aria-hidden="true" />
            <span className="ml-2 text-sm text-gray-500">Loading properties…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <AlertCircle size={24} className="mx-auto text-red-500 mb-3" aria-hidden="true" />
            <p className="text-sm text-red-700 mb-4">Unable to load properties.</p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : !data || data.length === 0 ? (
          <EmptyState icon={Building2} title="No properties yet" description="Listings appear here as operators create them — or use “Add Property” to create one on their behalf." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Properties list">
              <thead>
                <tr className="bg-gray-50">
                  {['Property', 'Operator', 'Location', 'Review Status', 'Active', ''].map((col, i) => (
                    <th
                      key={i}
                      scope="col"
                      className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-400">
                      No properties match your search.
                    </td>
                  </tr>
                ) : (
                  filtered.map((p) => (
                    <tr
                      key={p.id}
                      onClick={() => navigate(`/admin/properties/${p.id}?tab=overview`)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-brand-100 shrink-0" aria-hidden="true">
                            <Building2 size={16} className="text-brand-600" />
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/properties/${p.id}?tab=overview`);
                            }}
                            className="text-sm font-semibold text-gray-900 hover:text-brand-700 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
                          >
                            {p.name}
                          </button>
                          {p.hidden && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                              <EyeOff size={11} aria-hidden="true" /> Hidden
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">{p.operatorName}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <MapPin size={13} className="text-gray-400 shrink-0" aria-hidden="true" />
                          {p.city}, {p.country}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <ReviewStatusBadge status={p.reviewStatus} />
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={
                            p.isActive
                              ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-700'
                              : 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500'
                          }
                        >
                          {p.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleHidden(p.id, p.hidden);
                            }}
                            disabled={hideMutation.isPending || unhideMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                            aria-label={p.hidden ? `Unhide ${p.name}` : `Hide ${p.name}`}
                          >
                            {p.hidden ? (
                              <>
                                <Eye size={13} aria-hidden="true" /> Unhide
                              </>
                            ) : (
                              <>
                                <EyeOff size={13} aria-hidden="true" /> Hide
                              </>
                            )}
                          </button>
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-600">
                            Edit
                            <ChevronRight size={14} aria-hidden="true" />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
